import { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { getSale } from '../services/salesService';
import { formatDate, formatDateTime, formatMoney } from '../lib/format';
import { friendlyError } from '../lib/errors';
import LoadingState from '../components/ui/LoadingState';
import ErrorState from '../components/ui/ErrorState';
import EmptyState from '../components/ui/EmptyState';
import Badge from '../components/ui/Badge';

export default function SaleDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [sale, setSale] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setSale(await getSale(id));
    } catch (e) {
      setError(friendlyError(e));
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    load();
  }, [load]);

  if (loading) return <LoadingState label="Cargando venta…" />;
  if (error) return <ErrorState message={error} onRetry={load} />;
  if (!sale) return <EmptyState title="Venta no encontrada" />;

  const items = Array.isArray(sale.items) ? sale.items : [];

  return (
    <div className="page">
      <header className="page-header">
        <div>
          <p className="breadcrumb">
            <Link to="/admin/ventas">Ventas</Link> / #{sale.pedido}
          </p>
          <h1>Pedido #{sale.pedido}</h1>
          <p className="muted">
            {formatDate(sale.fecha)} · {sale.clienteNombre || sale.cliente || 'Cliente'}
          </p>
        </div>
        <div className="row-actions">
          <button type="button" className="btn btn-ghost" onClick={() => navigate(-1)}>
            Volver
          </button>
        </div>
      </header>

      <section className="stat-grid compact">
        <div className="stat-card">
          <p className="stat-label">Total</p>
          <p className="stat-value">{formatMoney(sale.total)}</p>
        </div>
        <div className="stat-card">
          <p className="stat-label">Estado</p>
          <p className="stat-value">
            <Badge tone={sale.status === 'pagado' ? 'ok' : 'neutral'}>{sale.status || '—'}</Badge>
          </p>
        </div>
        <div className="stat-card">
          <p className="stat-label">Medio de pago</p>
          <p className="stat-value" style={{ fontSize: '1rem' }}>
            {sale.medioPagoLabel || sale.medioPago || '—'}
          </p>
        </div>
        <div className="stat-card">
          <p className="stat-label">Comprobante</p>
          <p className="stat-value" style={{ fontSize: '1rem' }}>
            {sale.comprobanteEmitido
              ? `${sale.tipoComprobante || ''} ${sale.comprobanteSerie || ''}-${sale.comprobanteCorrelativo || ''}`
              : 'Sin emitir'}
          </p>
        </div>
      </section>

      <section className="meta-grid">
        <div>
          <span className="muted">Cliente doc</span>
          <div>{sale.cliente || '—'}</div>
        </div>
        <div>
          <span className="muted">Vendedor</span>
          <div>{sale.vendedor || '—'}</div>
        </div>
        <div>
          <span className="muted">Caja</span>
          <div>{sale.cajaId || '—'}</div>
        </div>
        <div>
          <span className="muted">Cancelado</span>
          <div>{sale.cancelado ? 'Sí' : 'No'}</div>
        </div>
        <div>
          <span className="muted">Fecha cobro</span>
          <div>{formatDateTime(sale.fechaCobro)}</div>
        </div>
        <div>
          <span className="muted">N° operación</span>
          <div>{sale.numeroOperacion || '—'}</div>
        </div>
      </section>

      <section className="panel">
        <header className="panel-header">
          <h2>Productos</h2>
          <span className="muted">{items.length} ítems</span>
        </header>
        {items.length === 0 ? (
          <EmptyState title="Sin ítems" />
        ) : (
          <div className="table-wrap">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Producto</th>
                  <th>Cant.</th>
                  <th>Precio</th>
                  <th>Subtotal</th>
                  <th>Lote</th>
                </tr>
              </thead>
              <tbody>
                {items.map((it, idx) => (
                  <tr key={`${it.uid || it.id || 'item'}-${idx}`}>
                    <td>{it.name || it.nombre || '—'}</td>
                    <td>{it.quantity ?? 0}</td>
                    <td>{formatMoney(it.finalPrice ?? it.price, it.currency)}</td>
                    <td>{formatMoney(Number(it.finalPrice ?? it.price ?? 0) * Number(it.quantity || 0), it.currency)}</td>
                    <td>{it.numeroLote || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
}
